import React, { useState } from 'react';
import {
  Box, Button, Badge, Drawer, Divider,
  FormControl, InputLabel, Select, MenuItem, Typography,
  Stack, useMediaQuery, useTheme,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { TaskStatus, TaskPriority, TaskCategory, STATUS_LABELS, PRIORITY_LABELS, CATEGORY_LABELS } from '../../types/task';
import { TaskFilters } from '../../hooks/useTaskManagement';

interface Props {
  filters:  TaskFilters;
  onChange: (filters: TaskFilters) => void;
}

function makeHandle(filters: TaskFilters, onChange: (f: TaskFilters) => void) {
  return (key: keyof TaskFilters) => (e: { target: { value: string } }) => {
    const val = e.target.value === '' ? undefined : e.target.value;
    onChange({ ...filters, [key]: val });
  };
}

const fcPropsFull   = { fullWidth: true, size: 'small' as const };
const fcPropsInline = { size: 'small' as const, sx: { minWidth: 168 } };

/** 絞り込み（ステータス・優先度・カテゴリ） */
function FilterFields({
  filters,
  onChange,
  fullWidth,
}: {
  filters:  TaskFilters;
  onChange: (filters: TaskFilters) => void;
  fullWidth: boolean;
}) {
  const handle  = makeHandle(filters, onChange);
  const fcProps = fullWidth ? fcPropsFull : fcPropsInline;

  return (
    <>
      <FormControl {...fcProps}>
        <InputLabel>ステータス</InputLabel>
        <Select value={filters.status ?? ''} label="ステータス" onChange={handle('status')}>
          <MenuItem value="">全ステータス</MenuItem>
          {Object.values(TaskStatus).map((s) => (
            <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl {...fcProps}>
        <InputLabel>優先度</InputLabel>
        <Select value={filters.priority ?? ''} label="優先度" onChange={handle('priority')}>
          <MenuItem value="">全優先度</MenuItem>
          {Object.values(TaskPriority).map((p) => (
            <MenuItem key={p} value={p}>{PRIORITY_LABELS[p]}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl {...fcProps}>
        <InputLabel>カテゴリ</InputLabel>
        <Select value={filters.category ?? ''} label="カテゴリ" onChange={handle('category')}>
          <MenuItem value="">全カテゴリ</MenuItem>
          {Object.values(TaskCategory).map((c) => (
            <MenuItem key={c} value={c}>{CATEGORY_LABELS[c]}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
}

/** 並び替え（並び順・順序） */
function SortFields({
  filters,
  onChange,
  fullWidth,
}: {
  filters:  TaskFilters;
  onChange: (filters: TaskFilters) => void;
  fullWidth: boolean;
}) {
  const handle  = makeHandle(filters, onChange);
  const fcProps = fullWidth ? fcPropsFull : fcPropsInline;

  return (
    <>
      <FormControl {...fcProps}>
        <InputLabel>並び順</InputLabel>
        <Select
          value={filters.sort_by ?? 'due_date'}
          label="並び順"
          onChange={handle('sort_by')}
        >
          <MenuItem value="created_at">作成日</MenuItem>
          <MenuItem value="due_date">締切日</MenuItem>
          <MenuItem value="priority">優先度</MenuItem>
        </Select>
      </FormControl>

      <FormControl {...fcProps}>
        <InputLabel>順序</InputLabel>
        <Select
          value={filters.sort_order ?? 'asc'}
          label="順序"
          onChange={handle('sort_order')}
        >
          <MenuItem value="asc">昇順</MenuItem>
          <MenuItem value="desc">降順</MenuItem>
        </Select>
      </FormControl>
    </>
  );
}

export const TaskFilterBar: React.FC<Props> = ({ filters, onChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const activeFilterCount = [filters.status, filters.priority, filters.category].filter(Boolean).length;
  const hasSortOverride     = filters.sort_by !== undefined || filters.sort_order !== undefined;
  const canReset            = activeFilterCount > 0 || hasSortOverride;

  const handleReset = () => {
    onChange({});
    setDrawerOpen(false);
  };

  // ── モバイル：ドロワー + バッジボタン ──────────────────────────────
  if (isMobile) {
    return (
      <>
        <Box sx={{ mb: 2 }}>
          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant="outlined"
              size="small"
              startIcon={<TuneIcon />}
              onClick={() => setDrawerOpen(true)}
            >
              絞り込み・並び替え
            </Button>
          </Badge>
        </Box>

        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { borderRadius: '16px 16px 0 0', px: 2, pb: 4, pt: 2 } }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={700}>絞り込み・並び替え</Typography>
            <Button onClick={() => setDrawerOpen(false)} sx={{ minWidth: 0, p: 0.5 }}>
              <CloseIcon />
            </Button>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FilterFields filters={filters} onChange={onChange} fullWidth />
            <SortFields filters={filters} onChange={onChange} fullWidth />
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
            <Button variant="outlined" fullWidth onClick={handleReset}>
              リセット
            </Button>
            <Button variant="contained" fullWidth onClick={() => setDrawerOpen(false)}>
              適用する
            </Button>
          </Box>
        </Drawer>
      </>
    );
  }

  // ── PC：インライン常時表示（1段目＝フィルター、2段目＝ソート＋リセット） ───
  return (
    <Box mb={2}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FilterFields filters={filters} onChange={onChange} fullWidth={false} />
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <SortFields filters={filters} onChange={onChange} fullWidth={false} />
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<FilterListOffIcon />}
            onClick={handleReset}
            disabled={!canReset}
            sx={{
              whiteSpace: 'nowrap',
              color: canReset ? 'text.primary' : 'text.disabled',
              borderColor: canReset ? 'text.secondary' : 'divider',
            }}
          >
            フィルターをリセット
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
